import React, { useState } from 'react';
import styled from 'styled-components';
import { Formik, Form, Field, ErrorMessage as FormikErrorMessage } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import Button from '../common/Button';
import { adminService } from '../../services/api';
import { LANGUAGES, languagesForRole } from '../../i18n/languages';

const FormContainer = styled.div`
  padding: ${({ theme }) => theme.spacing[2]};
`;

const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[4]};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const Input = styled(Field)`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}15;
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.backgroundLight};
    cursor: not-allowed;
  }
`;

const TextArea = styled(Field)`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  min-height: 80px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}15;
  }
`;

const Select = styled(Field)`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borders.radius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  background-color: white;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}15;
  }
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  margin-top: ${({ theme }) => theme.spacing[1]};
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-top: ${({ theme }) => theme.spacing[6]};
`;

const Hint = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: ${({ theme }) => theme.spacing[1]};
`;

// Divider-topped block for the role-specific profile fields (customer/employee)
const FormSection = styled.div`
  margin-top: ${({ theme }) => theme.spacing[5]};
  padding-top: ${({ theme }) => theme.spacing[4]};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  margin: 0 0 ${({ theme }) => theme.spacing[3]};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

// CVR input with the "Fetch from CVR" button beside it
const CvrRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};

  input {
    flex: 1;
    min-width: 0;
  }
`;

// Form validation schema. Add mode only allows admin/support (employees and
// customers are created from their own pages); Edit mode keeps all four roles
// and validates the role-specific profile section when present.
const buildUserSchema = ({ isEditing, isCustomerEdit, isEmployeeEdit }) => {
  const shape = {
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
    email: Yup.string().email('Invalid email address').required('Email is required'),
    role: Yup.string()
      .oneOf(isEditing ? ['admin', 'employee', 'customer', 'support'] : ['admin', 'support'], 'Invalid role')
      .required('Role is required')
  };

  if (!isEditing) {
    // Password is optional on create — blank means the backend generates one.
    shape.password = Yup.string().matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+])[A-Za-z\d!@#$%^&*()_+]{8,}$/,
      {
        message: 'Password must be at least 8 characters and contain at least one uppercase letter, one lowercase letter, one number, and one special character',
        excludeEmptyString: true
      }
    );
    shape.confirmPassword = Yup.string().when('password', ([password], schema) =>
      password && password.length > 0
        ? schema
            .oneOf([Yup.ref('password')], 'Passwords must match')
            .required('Confirm password is required')
        : schema
    );
  }

  if (isCustomerEdit) {
    shape.customer = Yup.object().shape({
      companyName: Yup.string().required('Company name is required'),
      address: Yup.string(),
      phoneNumber: Yup.string(),
      industry: Yup.string(),
      cvr: Yup.string().matches(/^\d{8}$/, { message: 'CVR must be 8 digits', excludeEmptyString: true }),
      invoiceEmail: Yup.string().email('Invalid email address')
    });
  }

  if (isEmployeeEdit) {
    shape.employee = Yup.object().shape({
      department: Yup.string(),
      position: Yup.string(),
      employeeCode: Yup.string(),
      startDate: Yup.string(),
      cpr: Yup.string(),
      hourlyRateEur: Yup.number()
        .typeError('Hourly rate must be a number')
        .min(0, 'Hourly rate cannot be negative')
        .nullable()
    });
  }

  return Yup.object().shape(shape);
};

const UserForm = ({ user, onSubmit, onCancel, isEditing, canManageSuperadmin = false }) => {
  const { t } = useTranslation();
  // Role-specific profile sections only exist in Edit mode — the sub-object is
  // included on the fetched user detail when the role matches (REQ feedback #8).
  const isCustomerEdit = !!(isEditing && user?.role === 'customer');
  const isEmployeeEdit = !!(isEditing && user?.role === 'employee');
  // An existing administrator still using a first-login credential must prove
  // ownership by completing password setup before gaining root privileges.
  // Intentional superadmin-on-create remains available to the acting owner.
  const superadminGrantBlocked = Boolean(
    isEditing && user?.isSuperAdmin !== true && user?.isFirstLogin === true
  );
  // Existing non-admin accounts must complete the role change as a separate
  // reviewed step. This prevents a passwordless employee/customer session from
  // inheriting root privileges in the same request.
  const existingTargetCanBeSuperadmin = !isEditing || user?.role === 'admin';

  // "Fetch from CVR" (official register lookup) state
  const [cvrLoading, setCvrLoading] = useState(false);
  const [cvrError, setCvrError] = useState(null);

  const handleCvrLookup = async (values, setFieldValue) => {
    const cvr = (values.customer?.cvr || '').trim();
    setCvrError(null);
    if (!/^\d{8}$/.test(cvr)) {
      setCvrError('Enter an 8-digit CVR number first');
      return;
    }
    try {
      setCvrLoading(true);
      const result = await adminService.lookupCvr(cvr);
      const company = result?.company;
      if (company?.name) setFieldValue('customer.companyName', company.name);
      if (company?.address) setFieldValue('customer.address', company.address);
    } catch (error) {
      setCvrError(error.message || 'CVR lookup failed. Please try again.');
    } finally {
      setCvrLoading(false);
    }
  };

  const initialValues = {
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    role: user?.role || 'admin',
    // Keep the backend's strict-boolean privilege boundary in the form too.
    isSuperAdmin: user?.isSuperAdmin === true,
    webauthnEnabled: user?.webauthnEnabled || false,
    language: user?.language || '',
    ...(!isEditing && { password: '', confirmPassword: '' }),
    ...(isCustomerEdit && {
      customer: {
        companyName: user?.customer?.companyName || '',
        address: user?.customer?.address || '',
        phoneNumber: user?.customer?.phoneNumber || '',
        industry: user?.customer?.industry || '',
        cvr: user?.customer?.cvr || '',
        invoiceEmail: user?.customer?.invoiceEmail || ''
      }
    }),
    ...(isEmployeeEdit && {
      employee: {
        department: user?.employee?.department || '',
        position: user?.employee?.position || '',
        employeeCode: user?.employee?.employeeCode || '',
        startDate: user?.employee?.startDate ? String(user.employee.startDate).slice(0, 10) : '',
        cpr: user?.employee?.cpr || '',
        hourlyRateEur: user?.employee?.hourlyRateEur ?? ''
      }
    })
  };

  return (
    <FormContainer>
      <Formik
        initialValues={initialValues}
        validationSchema={buildUserSchema({ isEditing, isCustomerEdit, isEmployeeEdit })}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, values, setFieldValue }) => (
          <Form>
            <FormRow>
              <FormGroup>
                <Label htmlFor="firstName">First Name</Label>
                <Input type="text" id="firstName" name="firstName" />
                <FormikErrorMessage name="firstName" component={ErrorMessage} />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="lastName">Last Name</Label>
                <Input type="text" id="lastName" name="lastName" />
                <FormikErrorMessage name="lastName" component={ErrorMessage} />
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label htmlFor="email">Email Address</Label>
              <Input
                type="email"
                id="email"
                name="email"
                disabled={isEditing}
              />
              <FormikErrorMessage name="email" component={ErrorMessage} />
              {isEditing && (
                <Hint>Email address cannot be changed</Hint>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="role">Role</Label>
              <Select forwardedAs="select" id="role" name="role">
                {isEditing ? (
                  <>
                    <option value="employee">Employee</option>
                    <option value="customer">Customer</option>
                    <option value="admin">Admin</option>
                    <option value="support">Support</option>
                  </>
                ) : (
                  <>
                    <option value="admin">Admin</option>
                    <option value="support">Support</option>
                  </>
                )}
              </Select>
              <FormikErrorMessage name="role" component={ErrorMessage} />
              {!isEditing && (
                <Hint>Employees and customers are created from the Employees / Customers pages.</Hint>
              )}
              {canManageSuperadmin === true
                && isEditing
                && user?.role !== 'admin'
                && values.role === 'admin' && (
                <Hint>{t('admin.users.superadmin.roleTransitionHint')}</Hint>
              )}
            </FormGroup>

            {/* Superadmin is a privilege on the admin role, not another role.
                The field is absent (rather than merely disabled) for ordinary
                admins and for non-admin targets. Request construction applies
                the same independent permission check. */}
            {canManageSuperadmin === true
              && existingTargetCanBeSuperadmin
              && values.role === 'admin' && (
              <FormGroup>
                <Label
                  htmlFor="isSuperAdmin"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    cursor: superadminGrantBlocked ? 'not-allowed' : 'pointer'
                  }}
                >
                  <Field
                    type="checkbox"
                    id="isSuperAdmin"
                    name="isSuperAdmin"
                    aria-describedby="superadmin-permission-hint"
                    disabled={superadminGrantBlocked}
                    style={{ width: 'auto' }}
                  />
                  {t('admin.users.superadmin.label')}
                </Label>
                <Hint id="superadmin-permission-hint">
                  {t('admin.users.superadmin.hint')}
                  {superadminGrantBlocked && (
                    <> {t('admin.users.superadmin.passwordSetupHint')}</>
                  )}
                </Hint>
              </FormGroup>
            )}

            {/* Preferred UI language — applied on the user's next login */}
            <FormGroup>
              <Label htmlFor="language">Preferred language</Label>
              <Select forwardedAs="select" id="language" name="language">
                <option value="">(Use browser default)</option>
                {languagesForRole(values.role).map((code) => (
                  <option key={code} value={code}>{LANGUAGES[code] ? LANGUAGES[code].nativeName : code}</option>
                ))}
              </Select>
              <Hint>The user sees the app in this language when they log in; they can change it in their profile.</Hint>
            </FormGroup>

            {/* Windows Hello / WebAuthn login — admins and support (REQ #21) */}
            {(values.role === 'admin' || values.role === 'support') && (
              <FormGroup>
                <Label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <Field type="checkbox" name="webauthnEnabled" style={{ width: 'auto' }} />
                  Enable Windows Hello login
                </Label>
                <Hint>
                  Lets this user enroll a passkey (Windows Hello / Touch ID) and sign in with it.
                  They set it up from the login screen / their own device.
                </Hint>
              </FormGroup>
            )}

            {/* Customer profile — shown when editing a customer user (feedback #8) */}
            {isCustomerEdit && (
              <FormSection>
                <SectionTitle>Company data</SectionTitle>

                <FormGroup>
                  <Label htmlFor="customer.companyName">Company Name</Label>
                  <Input type="text" id="customer.companyName" name="customer.companyName" />
                  <FormikErrorMessage name="customer.companyName" component={ErrorMessage} />
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="customer.address">Address</Label>
                  <TextArea forwardedAs="textarea" id="customer.address" name="customer.address" />
                  <FormikErrorMessage name="customer.address" component={ErrorMessage} />
                </FormGroup>

                <FormRow>
                  <FormGroup>
                    <Label htmlFor="customer.phoneNumber">Phone Number</Label>
                    <Input type="text" id="customer.phoneNumber" name="customer.phoneNumber" />
                    <FormikErrorMessage name="customer.phoneNumber" component={ErrorMessage} />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="customer.industry">Industry</Label>
                    <Input type="text" id="customer.industry" name="customer.industry" />
                    <FormikErrorMessage name="customer.industry" component={ErrorMessage} />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label htmlFor="customer.cvr">CVR</Label>
                    <CvrRow>
                      <Input type="text" id="customer.cvr" name="customer.cvr" />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        style={{ whiteSpace: 'nowrap' }}
                        onClick={() => handleCvrLookup(values, setFieldValue)}
                        disabled={cvrLoading}
                      >
                        {cvrLoading ? 'Fetching…' : 'Fetch from CVR'}
                      </Button>
                    </CvrRow>
                    <Hint>Danish CVR number — 8 digits. Fetch fills company name and address from the official register.</Hint>
                    {cvrError && <ErrorMessage>{cvrError}</ErrorMessage>}
                    <FormikErrorMessage name="customer.cvr" component={ErrorMessage} />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="customer.invoiceEmail">Invoice Email</Label>
                    <Input type="email" id="customer.invoiceEmail" name="customer.invoiceEmail" />
                    <FormikErrorMessage name="customer.invoiceEmail" component={ErrorMessage} />
                  </FormGroup>
                </FormRow>
              </FormSection>
            )}

            {/* Employee profile — shown when editing an employee user (feedback #8) */}
            {isEmployeeEdit && (
              <FormSection>
                <SectionTitle>Employee data</SectionTitle>

                <FormRow>
                  <FormGroup>
                    <Label htmlFor="employee.department">Department</Label>
                    <Input type="text" id="employee.department" name="employee.department" />
                    <FormikErrorMessage name="employee.department" component={ErrorMessage} />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="employee.position">Position</Label>
                    <Input type="text" id="employee.position" name="employee.position" />
                    <FormikErrorMessage name="employee.position" component={ErrorMessage} />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label htmlFor="employee.employeeCode">Employee Code</Label>
                    <Input type="text" id="employee.employeeCode" name="employee.employeeCode" />
                    <FormikErrorMessage name="employee.employeeCode" component={ErrorMessage} />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="employee.startDate">Start Date</Label>
                    <Input type="date" id="employee.startDate" name="employee.startDate" />
                    <FormikErrorMessage name="employee.startDate" component={ErrorMessage} />
                  </FormGroup>
                </FormRow>

                <FormRow>
                  <FormGroup>
                    <Label htmlFor="employee.cpr">CPR</Label>
                    <Input type="text" id="employee.cpr" name="employee.cpr" />
                    <FormikErrorMessage name="employee.cpr" component={ErrorMessage} />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="employee.hourlyRateEur">Hourly rate (EUR)</Label>
                    <Input type="number" step="0.01" min="0" id="employee.hourlyRateEur" name="employee.hourlyRateEur" />
                    <FormikErrorMessage name="employee.hourlyRateEur" component={ErrorMessage} />
                  </FormGroup>
                </FormRow>
              </FormSection>
            )}

            {/* Password fields only exist in Add mode — the update handler never
                sends a password (Reset Password covers that in Edit). Blank on
                create lets the backend generate one. */}
            {!isEditing && ['admin', 'support'].includes(values.role) && (
              <>
                <FormGroup>
                  <Label htmlFor="password">Password</Label>
                  <Input type="password" id="password" name="password" />
                  <FormikErrorMessage name="password" component={ErrorMessage} />
                  <Hint>
                    Optional — leave blank to let the system generate a password.
                    If set, it must be at least 8 characters with uppercase, lowercase, number, and special character.
                  </Hint>
                </FormGroup>

                {values.password && (
                  <FormGroup>
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <Input type="password" id="confirmPassword" name="confirmPassword" />
                    <FormikErrorMessage name="confirmPassword" component={ErrorMessage} />
                  </FormGroup>
                )}
              </>
            )}

            <FormActions>
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? isEditing ? 'Updating...' : 'Creating...'
                  : isEditing ? 'Update User' : 'Create User'}
              </Button>
            </FormActions>
          </Form>
        )}
      </Formik>
    </FormContainer>
  );
};

export default UserForm;
